import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Plus, RotateCw, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useDeleteSite, useEnvVars, useRedeploySite, useReplaceEnvVars, useRestartSite, useSite, useSiteLogs
} from "@/api/hooks";
import type { EnvVar } from "@/api/hostingApi";

export function SiteDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: site, isLoading } = useSite(id);
    const { data: logs } = useSiteLogs(id);
    const { data: savedEnvVars } = useEnvVars(id);

    const redeploy = useRedeploySite();
    const restart = useRestartSite();
    const del = useDeleteSite();
    const replaceEnvVars = useReplaceEnvVars(id ?? "");

    const [envVars, setEnvVars] = useState<EnvVar[] | null>(null);
    const editing = envVars ?? savedEnvVars ?? [];

    if (!id) return null;
    if (isLoading || !site) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    const addEnvVar = () => setEnvVars([...editing, { key: "", value: "" }]);
    const updateEnvVar = (i: number, field: keyof EnvVar, value: string) =>
        setEnvVars(editing.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));
    const removeEnvVar = (i: number) => setEnvVars(editing.filter((_, idx) => idx !== i));

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${site.name}"? This stops and removes its container permanently.`)) return;
        await del.mutateAsync(site.id);
        navigate("/");
    };

    return (
        <div className="mx-auto max-w-3xl p-6">
            <button
                type="button"
                onClick={() => navigate("/")}
                className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
                <ArrowLeft className="h-4 w-4" /> Back to sites
            </button>

            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h1 className="text-xl font-semibold">{site.name}</h1>
                    <a
                        href={`https://${site.subdomain}.hosted.athavita.com`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                        {site.subdomain}.hosted.athavita.com <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize">{site.status}</span>
            </div>

            {site.status === "failed" && site.lastError && (
                <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {site.lastError}
                </div>
            )}

            <div className="mb-6 flex gap-2">
                <Button variant="outline" onClick={() => redeploy.mutate(site.id)} disabled={redeploy.isPending}>
                    <Upload className="h-4 w-4" /> Redeploy
                </Button>
                <Button variant="outline" onClick={() => restart.mutate(site.id)} disabled={restart.isPending}>
                    <RotateCw className="h-4 w-4" /> Restart
                </Button>
                <Button variant="destructive" onClick={handleDelete} disabled={del.isPending}>
                    <Trash2 className="h-4 w-4" /> Delete
                </Button>
            </div>

            <section className="mb-6">
                <h2 className="mb-2 text-sm font-semibold">Environment variables</h2>
                <div className="flex flex-col gap-2">
                    {editing.map((v, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <Input placeholder="KEY" value={v.key} onChange={(e) => updateEnvVar(i, "key", e.target.value)} />
                            <Input placeholder="value" value={v.value} onChange={(e) => updateEnvVar(i, "value", e.target.value)} />
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeEnvVar(i)}>
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
                <div className="mt-2 flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={addEnvVar}>
                        <Plus className="h-3.5 w-3.5" /> Add
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        disabled={!envVars || replaceEnvVars.isPending}
                        onClick={() => envVars && replaceEnvVars.mutate(envVars.filter((v) => v.key.trim()), { onSuccess: () => setEnvVars(null) })}
                    >
                        Save (takes effect on next redeploy)
                    </Button>
                </div>
            </section>

            <section>
                <h2 className="mb-2 text-sm font-semibold">Logs</h2>
                <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
                    {logs || "No logs yet."}
                </pre>
            </section>
        </div>
    );
}
